import {
  Button,
  CreateButton,
  DeleteButton,
  EditButton,
  ExportButton,
  Icons,
  List,
  notification,
  ShowButton,
  Space,
  Table,
  TableProps,
  Tooltip,
  Typography,
} from '@pankod/refine-antd';
import { useCallback, useEffect, useState } from 'react';
import styles from './style.module.css';
import { CustomDatePicker, AddExpenseDrawer, SearchNew } from 'components';
import { IResourceComponentsProps, useNavigation } from '@pankod/refine-core';
import { getAllExpenses } from 'services/expense';
import { EditExpenseDrawer } from 'components/drawer/EditExpenseDrawer';
import { ShowExpenseDrawer } from 'components/drawer/ShowExpenseDrawer';
import { IExpense, PaginationHandler } from 'interfaces';
import { SearchIndex } from 'services/meilisearch';
import MetaData from 'utilities/metadata';
import { getDateRangeOfLastXMonths } from 'utilities/shared';
import {
  EXPENSE_CSV_HEADER,
  getExpenseExportRow,
  getExpenseRecord,
  IExpenseRecord,
} from 'utilities/expense-csv';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import CustomTableWithPagination from 'components/table/custom-table';
import { downloadCSVFile } from 'utilities/file';

const { Text } = Typography;

export const ExpenseList: React.FC<IResourceComponentsProps> = () => {
  const [expenses, setExpenses] = useState<IExpense[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<[Date, Date]>(getDateRangeOfLastXMonths(3));
  const [visible, setVisible] = useState<boolean>(false);
  const [editDrawerVisible, setEditDrawerVisible] = useState<boolean>(false);
  const [isShowDrawerVisible, setIsShowDrawerVisible] = useState<boolean>(false);
  const [expenseRecord, setExpenseRecord] = useState<IExpenseRecord>();
  const [totalExpenses, setTotalExpenses] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [isExportButtonLoading, setIsExportButtonLoading] = useState<boolean>(false);

  const [startDate, endDate] = dateRange;

  const { push } = useNavigation();
  const csvImportEnabled = useFeatureFlag('expense.csvImport');

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
  };

  const showEditDrawer = (record: IExpenseRecord) => {
    setEditDrawerVisible(true);
    setExpenseRecord(record);
  };

  const onEditClose = () => {
    setEditDrawerVisible(false);
  };

  const showDrawerVisible = (record: IExpenseRecord) => {
    setIsShowDrawerVisible(true);
    setExpenseRecord(record);
  };

  const onShowDrawerClose = () => {
    setIsShowDrawerVisible(false);
  };

  const fetchDataOnMountCallback = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getAllExpenses({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        searchString: searchText,
        start: page,
        limit,
      });
      const expenseData = data.data;
      setExpenses(expenseData);
      setTotalExpenses(data.total);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  }, [startDate, endDate, searchText, page, limit]);

  useEffect(() => {
    fetchDataOnMountCallback();
  }, [fetchDataOnMountCallback]);

  const onDeleteSuccessHandler = async () => {
    setIsLoading(true);
    try {
      const data = await getAllExpenses({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        searchString: searchText,
        start: page,
        limit,
      });
      const expenseData = data.data;
      setExpenses(expenseData);
    } catch (err) {
      return notification.error({
        message: 'Failed',
        description: 'Failed to delete.',
      });
    }
    setIsLoading(false);
  };

  const onSelectOption = async (searchText: string) => {
    setSearchText(searchText);
    setPage(1);
  };

  const onExportButtonClick = async () => {
    setIsExportButtonLoading(true);
    try {
      const rows = [EXPENSE_CSV_HEADER];
      const exportLimit = 100;
      let exportPage = 1;
      let total = 0;

      do {
        const response = await getAllExpenses({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          searchString: searchText,
          start: exportPage,
          limit: exportLimit,
        });
        total = response.total;
        if (!response.data.length) break;
        rows.push(...response.data.map(getExpenseRecord).map(getExpenseExportRow));
        exportPage += 1;
      } while (rows.length - 1 < total);

      downloadCSVFile(rows);
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to Export CSV File.',
      });
    }
    setIsExportButtonLoading(false);
  };

  const pageHeaders = (
    <>
      <CreateButton type="primary" onClick={showDrawer}></CreateButton>
      <CreateButton onClick={() => push('/expenses/expense-categories')}>Categories</CreateButton>
      <ExportButton loading={isExportButtonLoading} onClick={onExportButtonClick} />
      {csvImportEnabled && (
        <Button icon={<Icons.ImportOutlined />} onClick={() => push('/expenses/import')}>
          Import
        </Button>
      )}
    </>
  );

  const modifiedExpenseData: IExpenseRecord[] = expenses.map(getExpenseRecord);

  const onTableChange: TableProps<IExpenseRecord>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    setPage(pagination.current ?? 1);
    setLimit(pagination.pageSize ?? 10);
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  return (
    <>
      <MetaData />
      <List headerProps={{ extra: pageHeaders }}>
        <section className={styles['filter-section']}>
          <CustomDatePicker
            className={styles['filter-section__datePicker']}
            value={dateRange}
            onChange={(values) => {
              if (values && values[0] && values[1]) {
                setDateRange([new Date(values[0]), new Date(values[1])]);
              }
            }}
          />

          <SearchNew
            placeholder="Search by ID, Category, Warehouse"
            size="large"
            className={styles['filter-section__search']}
            index={SearchIndex.Expense}
            groups={['id', 'category', 'warehouse', 'client', 'supplier', 'user']}
            onSelectOption={onSelectOption}
            onReset={() => onSelectOption('')}
          />
        </section>
        <CustomTableWithPagination
          total={totalExpenses}
          page={page}
          limit={limit}
          onPageChange={onPageChange}
        >
          <Table
            dataSource={modifiedExpenseData}
            scroll={{ x: true }}
            loading={isLoading}
            onChange={onTableChange}
            // pagination={{
            //   defaultPageSize: 10,
            //   showSizeChanger: true,
            //   total: totalExpenses,
            //   current: page,
            // }}
            pagination={false}
          >
            <Table.Column dataIndex="id" title="Expense ID" />
            <Table.Column<IExpenseRecord>
              dataIndex="expenseDate"
              title="Expense Date"
              render={(_, record): React.ReactNode => {
                const date = new Date(record.expenseDate).toLocaleDateString('en-GB');
                return <Text>{date}</Text>;
              }}
            />
            <Table.Column dataIndex={['category', 'name']} title="Expense Category" />
            <Table.Column dataIndex="expenseWarehouse" title="Warehouse"></Table.Column>
            <Table.Column dataIndex="userName" title="Client/Supplier"></Table.Column>
            <Table.Column dataIndex="typeOfExpense" title="Transaction Type"></Table.Column>

            <Table.Column dataIndex="amount" title="Amount" />
            <Table.Column<IExpenseRecord>
              title="Actions"
              width={120}
              render={(_, record): React.ReactNode => {
                return (
                  <Space>
                    <ShowButton size="small" hideText onClick={() => showDrawerVisible(record)} />
                    <EditButton size="small" hideText onClick={() => showEditDrawer(record)} />
                    <DeleteButton
                      size="small"
                      hideText
                      recordItemId={record.id}
                      onSuccess={onDeleteSuccessHandler}
                    />
                    <Tooltip title="Test">
                      <Button size="small" icon={<Icons.InfoOutlined />} />
                    </Tooltip>
                  </Space>
                );
              }}
            />
          </Table>
        </CustomTableWithPagination>
      </List>
      {visible && (
        <AddExpenseDrawer
          onClose={onClose}
          visible={visible}
          title="Add Expense"
          onSuccess={fetchDataOnMountCallback}
        />
      )}
      {editDrawerVisible && expenseRecord && (
        <EditExpenseDrawer
          editVisible={editDrawerVisible}
          title="Edit Expense"
          onEditClose={onEditClose}
          record={expenseRecord}
          onSuccess={fetchDataOnMountCallback}
        />
      )}
      {isShowDrawerVisible && expenseRecord && (
        <ShowExpenseDrawer
          visible={isShowDrawerVisible}
          title="Show Expense"
          onClose={onShowDrawerClose}
          record={expenseRecord}
          setExpenses={setExpenses}
        />
      )}
    </>
  );
};
